import { trackError } from '@billy/track-error'
import { useMutation, useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { FecJournalMapping, updateFecJournalMapping, UpdateFecJournalMappingPayload } from '../query-api'
import type { FecJournalMappingWithImport } from '../types/fecJournalMappingWithImport'
import { getSiblingFecJournalMappings } from '../utils/getSiblingFecJournalMappings'

interface UpdateFecJournalMappingVariables {
  fecImportId: string
  mappingId: string
  payload: UpdateFecJournalMappingPayload
}

interface UpdatedFecJournalMapping {
  fecImportId: string
  mapping: FecJournalMapping
}

interface UseUpdateFecJournalMappingResult {
  pendingMappingId?: string
  update: (variables: UpdateFecJournalMappingVariables) => void
}

const trackSiblingUpdateError = (mappingId: string, error: unknown): void => {
  trackError(
    new Error(
      `FEC journal mapping update failed: ${mappingId}: ${error instanceof Error ? error.message : String(error)}`,
    ),
  )
}

const getCachedJournalMappings = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
): FecJournalMappingWithImport[] =>
  queryClient
    .getQueriesData<FecJournalMappingWithImport[]>([QueryKeys.FecJournalMappings, organizationId])
    .flatMap(([, mappings]) => mappings || [])

export const useUpdateFecJournalMapping = (): UseUpdateFecJournalMappingResult => {
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()

  const {
    isLoading,
    mutate: update,
    variables,
  } = useMutation(
    async ({
      fecImportId,
      mappingId,
      payload,
    }: UpdateFecJournalMappingVariables): Promise<UpdatedFecJournalMapping[]> => {
      const organizationId = organization?.id || ''
      const updatedMapping = await updateFecJournalMapping(organizationId, fecImportId, mappingId, payload)
      const siblings = getSiblingFecJournalMappings(
        getCachedJournalMappings(queryClient, organizationId),
        mappingId,
        updatedMapping.siblingIds,
      )
      const siblingResults = await Promise.allSettled(
        siblings.map(async (sibling) => ({
          fecImportId: sibling.fecImportId,
          mapping: await updateFecJournalMapping(organizationId, sibling.fecImportId, sibling.id, payload),
        })),
      )
      const siblingUpdates: UpdatedFecJournalMapping[] = []

      for (const [index, result] of siblingResults.entries()) {
        if (result.status === 'fulfilled') {
          siblingUpdates.push(result.value)
          continue
        }

        trackSiblingUpdateError(siblings[index].id, result.reason)
      }

      return [{ fecImportId, mapping: updatedMapping }, ...siblingUpdates]
    },
    {
      onError: (error: unknown, { mappingId }: UpdateFecJournalMappingVariables) => {
        trackSiblingUpdateError(mappingId, error)
      },
      onSuccess: (updatedMappings) => {
        for (const { fecImportId, mapping: updatedMapping } of updatedMappings) {
          queryClient.setQueriesData<Array<FecJournalMapping & { fecImportId?: string }> | undefined>(
            [QueryKeys.FecJournalMappings, organization?.id, fecImportId],
            (currentMappings) =>
              currentMappings?.map((mapping) =>
                mapping.id === updatedMapping.id ? { ...mapping, ...updatedMapping } : mapping,
              ),
          )
        }
      },
    },
  )

  return {
    pendingMappingId: isLoading ? variables?.mappingId : undefined,
    update,
  }
}
